/**
 * Hook for creating and managing a SharedTree view.
 */
import { Datapad as DatapadTree } from '@datapad/data-model';
import { assertNotUndefined } from '@datapad/utilities';
import {
	type IFluidContainer,
	type ITree,
	type TreeView,
	TreeViewConfiguration,
} from 'fluid-framework';
import React from 'react';

/**
 * TreeView configuration for the Datapad schema.
 */
const datapadTreeConfig = new TreeViewConfiguration({
	schema: DatapadTree,
	enableSchemaValidation: true,
});

/**
 * State returned by {@link useTreeView}.
 */
export interface TreeViewState {
	/**
	 * The tree view, or undefined if still loading or errored.
	 */
	readonly treeView: TreeView<typeof DatapadTree> | undefined;

	/**
	 * Whether the tree view is currently being created.
	 */
	readonly isLoading: boolean;

	/**
	 * Error that occurred during creation, if any.
	 */
	readonly error: Error | undefined;

	/**
	 * Whether the tree schema is incompatible with the stored data.
	 */
	readonly isIncompatible: boolean;
}

/**
 * Hook that creates and manages a SharedTree view from a Fluid container.
 *
 * @param container - The Fluid container containing the SharedTree, or undefined if not yet loaded.
 * @returns The tree view state including loading and error states.
 *
 * @remarks
 * This hook:
 * - Extracts the SharedTree from the container's initial objects
 * - Creates a typed view using the Datapad schema
 * - Properly disposes the view on cleanup (SharedTree only allows one view at a time)
 * - Detects schema incompatibility
 */
export function useTreeView(container: IFluidContainer | undefined): TreeViewState {
	const [treeView, setTreeView] = React.useState<TreeView<typeof DatapadTree> | undefined>(
		undefined,
	);
	const [isLoading, setIsLoading] = React.useState(true);
	const [error, setError] = React.useState<Error | undefined>(undefined);
	const [isIncompatible, setIsIncompatible] = React.useState(false);

	// Get the SharedTree from the container
	const tree = React.useMemo(() => {
		if (!container) {
			return undefined;
		}
		return assertNotUndefined(container.initialObjects.tree as ITree);
	}, [container]);

	// Create the view with proper lifecycle management
	React.useEffect(() => {
		if (!tree) {
			if (container) {
				// Container exists but tree doesn't - this is an error
				setError(new Error('SharedTree not found in container.'));
				setIsLoading(false);
			}
			return;
		}

		try {
			const newView = tree.viewWith(datapadTreeConfig);

			// Check compatibility
			if (!newView.compatibility.canView) {
				setIsIncompatible(true);
				setIsLoading(false);
				newView.dispose();
				return;
			}

			setTreeView(newView);
			setIsLoading(false);

			// Cleanup: dispose the view when unmounting or when tree changes
			return () => {
				newView.dispose();
			};
		} catch (error) {
			setError(error instanceof Error ? error : new Error(String(error)));
			setIsLoading(false);
		}
	}, [tree, container]);

	// Reset loading state when container changes
	React.useEffect(() => {
		if (!container) {
			setIsLoading(true);
			setTreeView(undefined);
			setError(undefined);
			setIsIncompatible(false);
		}
	}, [container]);

	return { treeView, isLoading: !container || isLoading, error, isIncompatible };
}
